import React, { useState, useEffect } from "react";
import DataTable from "../../../components/DataTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SchoolEntity } from "../../../services/schools/school.types";
import ActivityIndicator from "../../../components/ActivityIndicator";
import { Alert, Button, Tag, App } from "antd";
import {
  useGetSchoolsQuery,
  useDeleteSchoolMutation,
} from "../../../store/api/schoolsApi";
import TableSearchBar from "../../../components/TableSearchBar";
import { getApiErrorMessage } from "../../../utils";
import useDebounce from "../../../hooks/useDebounce";
import PageHeader from "../../../components/PageHeader";
import { useIsInitialLoad } from "../../../hooks/useIsInitialLoad";

const DEFAULT_PAGE_SIZE = 10;

const isCampus = (school: SchoolEntity): boolean => Boolean(school.parent_id);

/** Fila de la lista: una escuela principal con sus planteles anidados. */
type SchoolRow = SchoolEntity & {
  campus_count?: number;
  children?: SchoolEntity[];
};

const Schools: React.FC = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();

  // The URL is the source of truth for the filters and the (client-side)
  // pagination page: the list is deep-linkable and survives refreshes.
  // Inputs keep their own local state and sync into the URL after the
  // debounce below.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") ?? "", 10) || 1;
  const pageSize =
    parseInt(searchParams.get("pageSize") ?? "", 10) || DEFAULT_PAGE_SIZE;
  const idParam = searchParams.get("id") ?? "";
  const nameParam = searchParams.get("name") ?? "";

  const [searchId, setSearchId] = useState(idParam);
  const [searchName, setSearchName] = useState(nameParam);

  const debouncedSearchId = useDebounce(searchId, 500);
  const debouncedSearchName = useDebounce(searchName, 500);

  // Debounced inputs → URL. Changing a filter always resets to page 1.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const currentId = prev.get("id") ?? "";
        const currentName = prev.get("name") ?? "";
        if (
          currentId === debouncedSearchId &&
          currentName === debouncedSearchName
        ) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        if (debouncedSearchId) next.set("id", debouncedSearchId);
        else next.delete("id");
        if (debouncedSearchName) next.set("name", debouncedSearchName);
        else next.delete("name");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearchId, debouncedSearchName, setSearchParams]);

  // The backend has no search params for schools, so the query loads the
  // full list and the filtering below stays client-side. `data` (instead of
  // `currentData`) keeps the previous list visible while refetching.
  const { data, isFetching, isError, refetch } = useGetSchoolsQuery();
  const [deleteSchool] = useDeleteSchoolMutation();

  const schools = data ?? [];

  const matchesSearch = (school: SchoolEntity) =>
    school.name.toLowerCase().includes(nameParam.toLowerCase()) &&
    school.id.toString().includes(idParam);

  // SCRUM-916: un plantel ES una fila School con `parent_id`. La lista los
  // anida bajo su escuela principal en el cliente (el backend ya devuelve
  // `parent_id`), y el filtro busca TAMBIÉN dentro de los hijos: si sólo
  // matchea un plantel, su principal se mantiene visible como contenedor —
  // si no, buscar el nombre de un plantel no devolvería nada.
  const roots = schools.filter((school) => !school.parent_id);
  const filteredSchools: SchoolRow[] = roots
    .map((root): SchoolRow | null => {
      const children = schools.filter((school) => school.parent_id === root.id);
      const matchingChildren = children.filter(matchesSearch);
      const rootMatches = matchesSearch(root);
      if (!rootMatches && matchingChildren.length === 0) return null;
      const visibleChildren = rootMatches ? children : matchingChildren;
      return {
        ...root,
        campus_count: children.length,
        ...(visibleChildren.length > 0 ? { children: visibleChildren } : {}),
      };
    })
    .filter((school): school is SchoolRow => school !== null);

  const [collapsedIds, setCollapsedIds] = useState<number[]>([]);
  const expandedRowKeys = filteredSchools
    .filter(
      (school) =>
        (school.children?.length ?? 0) > 0 &&
        !collapsedIds.includes(school.id),
    )
    .map((school) => school.id);

  const isInitialLoad = useIsInitialLoad(isFetching, schools);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      next.set("pageSize", String(newPageSize));
      return next;
    });
  };

  const handleEdit = (record: SchoolEntity) => {
    navigate(`/schools/${record.id}`, { state: record.id });
  };

  const handleDelete = async (record: SchoolEntity) => {
    try {
      await deleteSchool(record.id).unwrap();
      notification["success"]({
        message: "Éxito",
        description: "La escuela fue eliminada exitosamente!",
        placement: "top",
        duration: 3,
      });
    } catch (error: any) {
      notification["error"]({
        message: "Eliminación Erronea",
        description: getApiErrorMessage(
          error,
          "La escuela no pudo ser eliminada."
        ),
        placement: "top",
        duration: 5,
      });
    }
  };

  return (
    <div>
      <PageHeader
        title="Escuelas"
        extra={
          <TableSearchBar
            fields={[
              {
                placeholder: "Buscar por ID",
                value: searchId,
                onChange: setSearchId,
                width: 200,
              },
              {
                placeholder: "Buscar por nombre",
                value: searchName,
                onChange: setSearchName,
                width: 200,
              },
            ]}
          >
            <Button type="primary" onClick={() => navigate("/schools/new")}>
              Nueva escuela
            </Button>
          </TableSearchBar>
        }
      />
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No se pudieron cargar las escuelas."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}
      {isInitialLoad ? (
        <ActivityIndicator />
      ) : (
        <DataTable
          data={filteredSchools}
          expandable={{
            expandedRowKeys,
            onExpand: (expanded, record) =>
              setCollapsedIds((previous) =>
                expanded
                  ? previous.filter((id) => id !== record.id)
                  : [...previous, record.id],
              ),
          }}
          columns={[
            { title: "ID", dataIndex: "id", key: "id" },
            { title: "Nombre", dataIndex: "name", key: "name" },
            {
              title: "Abreviatura",
              dataIndex: "abbreviation",
              key: "abbreviation",
            },
            { title: "CCT", dataIndex: "cct", key: "cct" },
            {
              // SCRUM-916: cuántos planteles cuelgan de esta escuela. En una
              // escuela sin planteles la celda queda vacía, no en "0".
              title: "Planteles",
              dataIndex: "campus_count",
              key: "campus_count",
              render: (count: number | undefined) => count || "",
            },
            {
              title: "Demo",
              dataIndex: "demo",
              key: "demo",
              render: (demo: boolean) => (demo ? "✅" : "❌"),
            },
            {
              title: "Tienda",
              dataIndex: "tienda_activa",
              key: "tienda_activa",
              // SCRUM-916: la tienda es configuración compartida y vive en la
              // escuela principal — un plantel la hereda, no la tiene propia.
              render: (v: boolean, record: SchoolEntity) =>
                isCampus(record) ? (
                  "—"
                ) : (
                  <Tag color={v ? "green" : "default"}>
                    {v ? "Activa" : "Sin tienda"}
                  </Tag>
                ),
            },
            {
              // SCRUM-748: a deactivated school stays listed here, clearly
              // marked. `undefined` (backend without the flag) reads as active.
              title: "Estado",
              dataIndex: "is_active",
              key: "is_active",
              // SCRUM-916: is_active también se hereda — el guard de login lee
              // la raíz, así que un plantel no tiene estado propio.
              render: (isActive: boolean | undefined, record: SchoolEntity) =>
                isCampus(record) ? (
                  "—"
                ) : (
                  <Tag
                    data-testid={`school-status-${record.id}`}
                    color={isActive === false ? "red" : "green"}
                  >
                    {isActive === false ? "Inactiva" : "Activa"}
                  </Tag>
                ),
            },
          ]}
          onEdit={handleEdit}
          onDelete={handleDelete}
          editButtonText="Administrar"
          loading={isFetching}
          pagination={{
            current: page,
            pageSize,
            total: filteredSchools.length,
            onChange: handlePageChange,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      )}
    </div>
  );
};

export default Schools;
